import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent,
  Chip,
  IconButton,
  Alert,
  useTheme,
  Tooltip
} from '@mui/material';
import { 
  Delete as DeleteIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';

function SelectedAccordsGrid({ selectedAccords, onRemoveAccord }) {
  const theme = useTheme();
  // Accords pre-selected for an uploaded case carry their similarity to it
  const hasPreselected = selectedAccords.some((accord) => accord.preselected);

  const similarityColumn = {
    field: 'similarity',
    headerName: 'Similaridade',
    width: 130,
    renderCell: (params) => (
      params.value == null ? (
        <Typography variant="body2" color="text.disabled">—</Typography>
      ) : (
        <Chip
          label={Number(params.value).toFixed(2).replace('.', ',')}
          size="small"
          variant="outlined"
          color="primary"
        />
      )
    ),
  };

  const baseColumns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          variant="outlined"
          color="primary"
        />
      ),
    },
    { 
      field: 'result', 
      headerName: 'Resultado', 
      width: 180,
      renderCell: (params) => {
        const getResultColor = (result) => {
          switch (result) {
            case "Provido": return "success";
            case "Não provido": return "error";
            case "Parcialmente provido": return "warning";
            case "Não conhecido": return "info";
            case "Mantido": return "primary";
            case "Rejeitado": return "secondary";
            default: return "default";
          }
        };
        
        return (
          <Chip 
            label={params.value} 
            size="small" 
            color={getResultColor(params.value)}
            variant="filled"
          />
        );
      },
    },
    { 
      field: 'ementa', 
      headerName: 'Ementa', 
      width: 400,
      renderCell: (params) => (
        <Tooltip title={params.value || ''} arrow placement="bottom-start">
          <Typography 
            variant="body2" 
            sx={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              cursor: 'help',
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    { 
      field: 'penalidade', 
      headerName: 'Penalidade', 
      width: 150,
      renderCell: (params) => (
        <Tooltip title={params.value || ''} arrow placement="bottom-start">
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              cursor: 'help',
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    { 
      field: 'baseLegal', 
      headerName: 'Base Legal', 
      width: 200,
      renderCell: (params) => (
        <Tooltip title={params.value || ''} arrow placement="bottom-start">
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              cursor: 'help',
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          color="error"
          onClick={() => onRemoveAccord(params.row.id)}
          size="small"
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(211, 47, 47, 0.04)',
            },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // Similarity after the ID when there is a pre-selection; no remove button where removal is not offered
  const columns = [
    baseColumns[0],
    ...(hasPreselected ? [similarityColumn] : []),
    ...baseColumns.slice(1),
  ].filter((column) => column.field !== 'actions' || typeof onRemoveAccord === 'function');

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Acórdãos Selecionados
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={`${selectedAccords.length}/10 selecionados`}
              color={selectedAccords.length >= 10 ? "error" : "primary"}
              variant="outlined"
            />
          </Box>
        </Box>

        {selectedAccords.length >= 10 && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2, borderRadius: 2 }}
          >
            Limite máximo de 10 acórdãos atingido. Para selecionar novos acórdãos, remova alguns da lista.
          </Alert>
        )}

        {hasPreselected && (
          <Alert 
            severity="info" 
            sx={{ mb: 2, borderRadius: 2 }}
          >
            Os acórdãos com similaridade indicada foram pré-selecionados por serem os mais semelhantes ao caso carregado. Confirme ou altere a seleção: remova acórdãos da lista e adicione outros clicando nos pontos do mapa.
          </Alert>
        )}

        {selectedAccords.length === 0 ? (
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 6,
              backgroundColor: theme.palette.background.default,
              borderRadius: 2,
              border: `2px dashed ${theme.palette.divider}`,
            }}
          >
            <InfoIcon sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nenhum acórdão selecionado
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Clique nos pontos do mapa para selecionar acórdãos para análise
            </Typography>
          </Box>
        ) : (
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={selectedAccords}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 5 },
                },
              }}
              pageSizeOptions={[5, 10]}
              disableRowSelectionOnClick
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': {
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: theme.palette.background.default,
                  borderBottom: `2px solid ${theme.palette.divider}`,
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: `${theme.palette.primary.main}08`,
                },
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default SelectedAccordsGrid;
